import { motion } from 'motion/react';
import styled, { keyframes } from 'styled-components';

export type ToastType = 'success' | 'error' | 'info';

const shrink = keyframes`
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
`;

export const ToastrContainer = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 999999;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  /* Above the mobile tab bar, and clear of the home indicator. */
  @media (max-width: 768px) {
    left: 12px;
    right: 12px;
    bottom: calc(76px + env(safe-area-inset-bottom, 0px));
    transform: none;
    align-items: stretch;
  }
`;

// The severity travels as `data-type` rather than a styled prop: this wraps `motion.div`, and
// styled-components only filters custom props out for plain tags — a `$prop` would reach the DOM.
export const Toast = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 260px;
  max-width: min(440px, calc(100vw - 24px));
  padding: 13px 14px;
  border-radius: 14px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  box-shadow:
    0 12px 32px -12px rgba(16, 24, 40, 0.28),
    0 2px 6px -2px rgba(16, 24, 40, 0.12);
  color: var(--main-black);
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y;

  &:active {
    cursor: grabbing;
  }

  &[data-type='success'] {
    --toast-accent: #16a34a;
    --toast-tint: rgba(22, 163, 74, 0.12);
  }

  &[data-type='error'] {
    --toast-accent: #dc2626;
    --toast-tint: rgba(220, 38, 38, 0.12);
  }

  &[data-type='info'] {
    --toast-accent: #2563eb;
    --toast-tint: rgba(37, 99, 235, 0.12);
  }

  [data-theme='dark'] & {
    box-shadow: 0 12px 32px -10px rgba(0, 0, 0, 0.55);

    &[data-type='success'] {
      --toast-accent: #4ade80;
      --toast-tint: rgba(74, 222, 128, 0.16);
    }

    &[data-type='error'] {
      --toast-accent: #f87171;
      --toast-tint: rgba(248, 113, 113, 0.16);
    }

    &[data-type='info'] {
      --toast-accent: #60a5fa;
      --toast-tint: rgba(96, 165, 250, 0.16);
    }
  }

  @media (max-width: 768px) {
    min-width: 0;
    max-width: none;
  }
`;

export const ToastIcon = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--toast-tint);
  color: var(--toast-accent);

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.75;
  }
`;

export const ToastMessage = styled.p`
  flex: 1 1 auto;
  padding-top: 2px;
  font-size: var(--font-size-medium);
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const ToastClose = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--hover-background);
    color: var(--main-black);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

type ProgressProps = { $duration: number; $isPaused: boolean };

export const ToastProgress = styled.span<ProgressProps>`
  position: absolute;
  inset: auto 0 0 0;
  height: 2px;
  background: var(--toast-accent);
  opacity: 0.5;
  transform-origin: left center;
  animation: ${shrink} ${({ $duration }: ProgressProps) => $duration}ms linear forwards;
  animation-play-state: ${({ $isPaused }: ProgressProps) => ($isPaused ? 'paused' : 'running')};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
